/**
 * Input Event Handler
 * 
 * Handles real-time input events with debounced processing for field formatting.
 * Provides responsive formatting while maintaining optimal performance during
 * rapid typing sequences.
 */

import { FieldType } from '../../detection-patterns';
import { EventContext, EventProcessingResult, EventHandler } from '../event-manager';

/**
 * Input processing configuration
 */
export interface InputProcessingConfig {
  /** Whether to apply formatting in real-time */
  realTimeFormatting: boolean;
  /** Whether to preserve cursor position */
  preserveCursorPosition: boolean;
  /** Whether to validate while typing */
  validateWhileTyping: boolean;
  /** Maximum input length for the field type */
  maxLength?: number;
  /** Input filtering regex pattern */
  inputFilter?: RegExp;
  /** Whether to allow composition events (IME input) */
  allowComposition: boolean;
}

/**
 * Default configurations for different field types
 */
const DEFAULT_CONFIGS: Record<FieldType, InputProcessingConfig> = {
  [FieldType.PHONE]: {
    realTimeFormatting: true,
    preserveCursorPosition: true,
    validateWhileTyping: false,
    maxLength: 14, // (XXX) XXX-XXXX
    inputFilter: /[0-9()\-\s]/,
    allowComposition: false
  },
  [FieldType.ZIP]: {
    realTimeFormatting: true,
    preserveCursorPosition: true,
    validateWhileTyping: false,
    maxLength: 10, // XXXXX-XXXX
    inputFilter: /[0-9\-]/,
    allowComposition: false
  },
  [FieldType.DATE_OF_BIRTH]: {
    realTimeFormatting: true,
    preserveCursorPosition: true,
    validateWhileTyping: false,
    maxLength: 10, // MM/DD/YYYY
    inputFilter: /[0-9\/]/,
    allowComposition: false
  },
  [FieldType.CURRENCY]: {
    realTimeFormatting: true,
    preserveCursorPosition: true,
    validateWhileTyping: false,
    maxLength: 15, // $XXX,XXX.XX
    inputFilter: /[0-9$,.\s]/,
    allowComposition: false
  },
  [FieldType.SSN]: {
    realTimeFormatting: false, // SSN formatting only on blur for security
    preserveCursorPosition: true,
    validateWhileTyping: false,
    maxLength: 11, // XXX-XX-XXXX
    inputFilter: /[0-9\-]/,
    allowComposition: false
  },
  [FieldType.NAME]: {
    realTimeFormatting: false,
    preserveCursorPosition: true,
    validateWhileTyping: true, // Validate spaces while typing
    maxLength: 100,
    inputFilter: /[a-zA-Z\s\-'.]/, // Allow letters, spaces, hyphens, apostrophes, dots
    allowComposition: true
  },
  [FieldType.UNKNOWN]: {
    realTimeFormatting: false,
    preserveCursorPosition: true,
    validateWhileTyping: false,
    allowComposition: true
  }
};

/**
 * Input validation result
 */
interface InputValidationResult {
  isValid: boolean;
  filteredValue: string;
  errors: string[];
  shouldReject: boolean;
}

/**
 * Create an input event handler for a specific field type
 * 
 * @param fieldType The type of field to handle
 * @param customConfig Optional configuration overrides
 * @returns Event handler function
 */
export function createInputHandler(
  fieldType: FieldType,
  customConfig?: Partial<InputProcessingConfig>
): EventHandler {
  const config = { ...DEFAULT_CONFIGS[fieldType], ...customConfig };

  return async (context: EventContext): Promise<EventProcessingResult> => {
    const startTime = performance.now();
    const { field, element, value, previousValue, isComposition, cursorPosition } = context;

    try {
      // Skip processing during composition if not allowed
      if (isComposition && !config.allowComposition) {
        return {
          success: true,
          processingTime: performance.now() - startTime
        };
      }

      // Validate and filter input
      const validation = validateInput(value, config, fieldType);
      if (validation.shouldReject) {
        // Restore previous value
        return {
          success: true,
          newValue: previousValue || '',
          newCursorPosition: Math.max(0, (cursorPosition || 0) - 1),
          processingTime: performance.now() - startTime,
          errors: validation.errors
        };
      }

      let processedValue = validation.filteredValue;
      let newCursorPosition = cursorPosition;
      let formatApplied = false;

      // Apply real-time formatting if enabled
      if (config.realTimeFormatting && processedValue.length > 0) {
        const formatResult = await formatFieldValue(processedValue, fieldType, cursorPosition);
        if (formatResult.success && formatResult.formattedValue !== processedValue) {
          processedValue = formatResult.formattedValue;
          newCursorPosition = formatResult.newCursorPosition;
          formatApplied = true;
        }
      }

      // Validate while typing if enabled
      let errors: string[] = [];
      if (config.validateWhileTyping && processedValue.length > 0) {
        const validationResult = await validateFieldValue(processedValue, fieldType);
        errors = validationResult.errors || [];
      }

      return {
        success: true,
        newValue: processedValue,
        newCursorPosition: config.preserveCursorPosition ? newCursorPosition : undefined,
        formatApplied,
        errors,
        processingTime: performance.now() - startTime
      };

    } catch (error) {
      return {
        success: false,
        errors: [`Input processing error: ${error}`],
        processingTime: performance.now() - startTime
      };
    }
  };
}

/**
 * Validate and filter input value
 */
function validateInput(
  value: string,
  config: InputProcessingConfig,
  fieldType: FieldType
): InputValidationResult {
  const errors: string[] = [];
  let filteredValue = value;
  let shouldReject = false;

  // Check maximum length
  if (config.maxLength && value.length > config.maxLength) {
    filteredValue = value.substring(0, config.maxLength);
  }

  // Apply input filter
  if (config.inputFilter && value.length > 0) {
    const originalLength = filteredValue.length;
    filteredValue = filteredValue.split('').filter(char => config.inputFilter!.test(char)).join('');
    
    // If characters were filtered out, consider rejecting
    if (filteredValue.length < originalLength && originalLength === value.length) {
      // Only reject if the last character was invalid (real-time typing)
      const lastChar = value.charAt(value.length - 1);
      if (!config.inputFilter.test(lastChar)) {
        shouldReject = true;
        errors.push(`Invalid character '${lastChar}' for ${fieldType} field`);
      }
    }
  }

  return {
    isValid: errors.length === 0 && !shouldReject,
    filteredValue,
    errors,
    shouldReject
  };
}

/**
 * Format field value with cursor position preservation
 */
async function formatFieldValue(
  value: string,
  fieldType: FieldType,
  cursorPosition?: number
): Promise<{
  success: boolean;
  formattedValue: string;
  newCursorPosition?: number;
  errors?: string[];
}> {
  try {
    // This will be integrated with the format pattern library (Issue #30)
    // For now, implement basic formatting patterns

    let formattedValue = value;
    let newCursorPosition = cursorPosition;

    switch (fieldType) {
      case FieldType.PHONE:
        const phoneResult = formatPhone(value, cursorPosition);
        formattedValue = phoneResult.formatted;
        newCursorPosition = phoneResult.cursorPosition;
        break;

      case FieldType.ZIP:
        const zipResult = formatZip(value, cursorPosition);
        formattedValue = zipResult.formatted;
        newCursorPosition = zipResult.cursorPosition;
        break;

      case FieldType.DATE_OF_BIRTH:
        const dateResult = formatDate(value, cursorPosition);
        formattedValue = dateResult.formatted;
        newCursorPosition = dateResult.cursorPosition;
        break;

      case FieldType.CURRENCY:
        const currencyResult = formatCurrency(value, cursorPosition);
        formattedValue = currencyResult.formatted;
        newCursorPosition = currencyResult.cursorPosition;
        break;

      case FieldType.NAME:
        // No real-time formatting for name, but we could add capitalization logic here
        break;

      default:
        // No formatting for other types during input
        break;
    }

    return {
      success: true,
      formattedValue,
      newCursorPosition
    };

  } catch (error) {
    return {
      success: false,
      formattedValue: value,
      errors: [`Formatting error: ${error}`]
    };
  }
}

/**
 * Validate field value
 */
async function validateFieldValue(
  value: string,
  fieldType: FieldType
): Promise<{ errors?: string[] }> {
  // This will be integrated with the validation system
  // For now, return no errors (validation on blur) except for NAME
  if (fieldType === FieldType.NAME) {
    if (value.includes(' ')) {
      return { errors: ['Spaces are not allowed in this field'] };
    }
  }
  return { errors: [] };
}

/**
 * Basic phone number formatting with cursor position tracking
 */
function formatPhone(value: string, cursorPosition?: number): { formatted: string; cursorPosition?: number } {
  // Remove all non-digit characters for processing
  const digits = value.replace(/\D/g, '');
  
  if (digits.length === 0) {
    return { formatted: '', cursorPosition };
  }

  let formatted = '';
  let newCursorPosition = cursorPosition;

  if (digits.length <= 3) {
    formatted = `(${digits}`;
    if (cursorPosition !== undefined && cursorPosition <= digits.length) {
      newCursorPosition = cursorPosition + 1; // Account for opening parenthesis
    }
  } else if (digits.length <= 6) {
    formatted = `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    if (cursorPosition !== undefined) {
      if (cursorPosition <= 3) {
        newCursorPosition = cursorPosition + 1; // Account for opening parenthesis
      } else if (cursorPosition <= 6) {
        newCursorPosition = cursorPosition + 3; // Account for ") "
      }
    }
  } else {
    formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    if (cursorPosition !== undefined) {
      if (cursorPosition <= 3) {
        newCursorPosition = cursorPosition + 1; // Account for opening parenthesis
      } else if (cursorPosition <= 6) {
        newCursorPosition = cursorPosition + 3; // Account for ") "
      } else if (cursorPosition <= 10) {
        newCursorPosition = cursorPosition + 4; // Account for ") " and "-"
      }
    }
  }

  return { formatted, cursorPosition: newCursorPosition };
}

/**
 * Basic ZIP code formatting with cursor position tracking
 */
function formatZip(value: string, cursorPosition?: number): { formatted: string; cursorPosition?: number } {
  const digits = value.replace(/\D/g, '');
  
  if (digits.length === 0) {
    return { formatted: '', cursorPosition };
  }

  let formatted = digits;
  let newCursorPosition = cursorPosition;

  if (digits.length > 5) {
    formatted = `${digits.slice(0, 5)}-${digits.slice(5, 9)}`;
    if (cursorPosition !== undefined && cursorPosition > 5) {
      newCursorPosition = cursorPosition + 1; // Account for dash
    }
  }

  return { formatted, cursorPosition: newCursorPosition };
}

/**
 * Basic date formatting with cursor position tracking (MM/DD/YYYY)
 */
function formatDate(value: string, cursorPosition?: number): { formatted: string; cursorPosition?: number } {
  const digits = value.replace(/\D/g, '');
  
  if (digits.length === 0) {
    return { formatted: '', cursorPosition };
  }

  let formatted = '';
  let newCursorPosition = cursorPosition;

  if (digits.length <= 2) {
    formatted = digits;
  } else if (digits.length <= 4) {
    formatted = `${digits.slice(0, 2)}/${digits.slice(2)}`;
    if (cursorPosition !== undefined && cursorPosition > 2) {
      newCursorPosition = cursorPosition + 1; // Account for first slash
    }
  } else {
    formatted = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
    if (cursorPosition !== undefined) {
      if (cursorPosition > 2 && cursorPosition <= 4) {
        newCursorPosition = cursorPosition + 1; // Account for first slash
      } else if (cursorPosition > 4) {
        newCursorPosition = cursorPosition + 2; // Account for both slashes
      }
    }
  }

  return { formatted, cursorPosition: newCursorPosition };
}

/**
 * Basic currency formatting with cursor position tracking
 */
function formatCurrency(value: string, cursorPosition?: number): { formatted: string; cursorPosition?: number } {
  // Remove all non-digit and non-decimal characters
  const cleaned = value.replace(/[^\d.]/g, '');
  
  if (cleaned.length === 0) {
    return { formatted: '', cursorPosition };
  }

  // Parse as number and format with commas
  const num = parseFloat(cleaned);
  if (isNaN(num)) {
    return { formatted: value, cursorPosition };
  }

  // Basic formatting - add commas for thousands
  let formatted = num.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  });

  // Add dollar sign if not present
  if (!formatted.startsWith('$')) {
    formatted = `$${formatted}`;
  }

  // Cursor position adjustment is complex for currency
  // This is a simplified version - full implementation in cursor management
  return { formatted, cursorPosition };
}

/**
 * Get default configuration for field type
 */
export function getDefaultInputConfig(fieldType: FieldType): InputProcessingConfig {
  return { ...DEFAULT_CONFIGS[fieldType] };
}

/**
 * Check if field type supports real-time formatting
 */
export function supportsRealTimeFormatting(fieldType: FieldType): boolean {
  return DEFAULT_CONFIGS[fieldType]?.realTimeFormatting || false;
}